'use client'

import { m } from 'framer-motion'
import { ArrowRight, Play } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'

function mulberry32(seed: number) {
  let t = seed >>> 0
  return function rand() {
    t += 0x6d2b79f5
    let x = t
    x = Math.imul(x ^ (x >>> 15), x | 1)
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61)
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296
  }
}

interface HeroProps {
  title?: React.ReactNode
  subtitle?: string
  disableBackground?: boolean
  badgeText?: string
  primaryCtaLabel?: string
  primaryCtaHref?: string
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  stats?: Array<{ value: string; label: string }>
}

export default function Hero({
  title = (
    <>
      Создаём
      <br />
      <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
        цифровые продукты,
      </span>
      <br />
      <span className="text-white/90">которые продают</span>
    </>
  ),
  subtitle = 'Сайты, ИИ-решения, маркетинг и SMM — комплексный подход для вашего бизнеса. От идеи до первых продаж за 30 дней.',
  disableBackground = false,
  badgeText = 'Digital-агентство полного цикла',
  primaryCtaLabel = 'Обсудить проект',
  primaryCtaHref = '/contacts',
  secondaryCtaLabel = 'Наши кейсы',
  secondaryCtaHref = '/cases',
  stats = [
    { value: '150+', label: 'Проектов' },
    { value: '50+', label: 'Клиентов' },
    { value: '10+ лет', label: 'Опыта' },
  ],
}: HeroProps) {
  const makeParticles = useMemo(() => {
    const count = 20
    const baseSeed = 1337
    return () =>
      Array.from({ length: count }, (_, i) => {
        const r = mulberry32(baseSeed + i * 101)
        return {
          left: `${(r() * 100).toFixed(6)}%`,
          top: `${(r() * 100).toFixed(6)}%`,
          duration: 3 + r() * 2,
          delay: r() * 2,
        }
      })
  }, [])

  const [particles, setParticles] = useState<
    Array<{ left: string; top: string; duration: number; delay: number }>
  >([])

  useEffect(() => {
    setParticles(makeParticles())
  }, [makeParticles])

  return (
    <section className="hero-mobile-safe relative flex min-h-[100svh] items-center justify-center overflow-hidden px-4 py-0">
      {!disableBackground && (
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/2 h-[600px] w-[1000px] -translate-x-1/2 rounded-full bg-gradient-to-r from-purple-600/25 via-pink-600/20 to-blue-600/25 blur-[200px]" />
          <div className="absolute bottom-1/4 left-1/4 h-[500px] w-[500px] rounded-full bg-blue-600/15 blur-[150px]" />
          <div className="absolute right-1/4 bottom-1/3 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[120px]" />

          {particles.map((p, i) => (
            <m.div
              key={i}
              className="absolute h-1 w-1 rounded-full bg-white/30"
              style={{ left: p.left, top: p.top }}
              animate={{ y: [0, -30, 0], opacity: [0.2, 0.6, 0.2] }}
              transition={{ duration: p.duration, repeat: Infinity, delay: p.delay }}
            />
          ))}
        </div>
      )}

      <div className="home-container relative z-10 text-center">
        <div>
          {/*
            The hero entrance used framer-motion (initial opacity:0) here,
            which meant the LCP text was invisible until JS hydrated and the
            animation ran — pushing LCP past 2.5s on a cold load. CSS
            animations start at first paint regardless of hydration, so this
            keeps the same fade-up look without gating visibility on JS.
          */}
          <div className="animate-fade-in-up mb-4 inline-flex items-center gap-3 rounded-full border border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-pink-500/10 px-5 py-2.5 sm:mb-7 md:mb-8 [@media(max-height:820px)]:md:mb-4 [@media(max-height:980px)]:md:mb-6">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-purple-500"></span>
            </span>
            <span className="bg-gradient-to-r from-purple-200 to-pink-200 bg-clip-text text-sm font-medium tracking-wide text-transparent">
              {badgeText}
            </span>
          </div>

          <h1
            className="home-hero-title animate-fade-in-up mb-4 sm:mb-6 md:mb-7 md:text-[clamp(3.35rem,11.2vw,7.55rem)] [@media(max-height:820px)]:md:mb-3 [@media(max-height:980px)]:md:mb-5"
            style={{ animationDelay: '0.1s' }}
          >
            {title}
          </h1>

          <p
            className="home-hero-lead animate-fade-in-up mx-auto mb-5 max-w-3xl text-gray-400 sm:mb-8 md:mb-10 [@media(max-height:820px)]:md:mb-5 [@media(max-height:980px)]:md:mb-7"
            style={{ animationDelay: '0.2s' }}
          >
            {subtitle}
          </p>

          <div
            className="animate-fade-in-up mb-5 flex flex-col justify-center gap-3 sm:mb-9 sm:flex-row sm:gap-4 md:mb-14 [@media(max-height:820px)]:md:mb-7 [@media(max-height:980px)]:md:mb-10"
            style={{ animationDelay: '0.3s' }}
          >
            <Link
              href={primaryCtaHref}
              className="group flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 text-base font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_60px_rgba(168,85,247,0.5)] md:px-9 md:py-4 md:text-[1.02rem] [@media(max-height:820px)]:md:py-3 [@media(max-height:980px)]:md:px-8 [@media(max-height:980px)]:md:py-3.5"
            >
              {primaryCtaLabel}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              href={secondaryCtaHref}
              className="group flex items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-8 py-3 text-base transition-all duration-300 hover:border-white/20 hover:bg-white/10 md:px-9 md:py-4 md:text-[1.02rem] [@media(max-height:820px)]:md:py-3 [@media(max-height:980px)]:md:px-8 [@media(max-height:980px)]:md:py-3.5"
            >
              <Play className="h-5 w-5" />
              {secondaryCtaLabel}
            </Link>
          </div>
        </div>

        <div
          className="hero-stats-3 animate-fade-in-up mt-2 flex flex-wrap justify-center gap-4 pb-1 sm:gap-7 md:gap-20 md:pb-0 [@media(max-height:820px)]:md:gap-10 [@media(max-height:980px)]:md:gap-14"
          style={{ animationDelay: '0.5s' }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="animate-fade-in-up min-w-[100px] text-center"
              style={{ animationDelay: `${0.6 + i * 0.1}s` }}
            >
              <p className="hero-stat-value mb-2 bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">
                {stat.value}
              </p>
              <p className="hero-stat-label text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-11 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex [@media(max-height:820px)]:hidden [@media(max-height:980px)]:bottom-8"
      >
        <span className="text-xs tracking-widest text-gray-500 uppercase">Листать</span>
        <m.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="h-10 w-[1px] bg-gradient-to-b from-white/50 to-transparent"
        />
      </m.div>
    </section>
  )
}
